import Button from "components/Button";
import React, { useCallback, useMemo, useRef, useState } from "react";
import useStore from "utils/useStore";
import EditButton from "./editButton";
import DropdownComponent from "components/Dropdown";

import { SelectableItem } from "utils/useStore";
import styles from "./summary.module.css";

import constants from "constants/fi.json";
import AddButton from "./addButton";

const useEditing = (defaultIsEditing: boolean = false) => {
  const [isEditing, setIsEditing] = useState<boolean>(defaultIsEditing);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const toggleEditing = useCallback(() => {
    setIsEditing((oldValue) => !oldValue);
  }, []);

  const Edit = () => (
    <EditButton onClick={() => toggleEditing()} isEditing={isEditing} />
  );

  const Add = () => (
    <AddButton onClick={() => toggleEditing()} isAdding={isEditing} />
  );

  const UnEditable = ({ children }: { children: React.ReactNode }) => {
    return (
      <div onClick={() => setModalOpen(true)} className={styles.editingValue}>
        {children}
      </div>
    );
  };

  const closeModal = () => setModalOpen(false);

  const ConfirmCancel = ({
    confirm,
    disabled,
  }: {
    confirm?: () => void;
    disabled?: boolean;
  }) => {
    if (!isEditing) {
      return null;
    }

    const classes = [styles.confirmCancel];

    if (disabled) {
      classes.push(styles.disabled);
    }

    return (
      isEditing && (
        <div className={classes.join(" ")}>
          <Button
            tiny
            secondary
            onClick={() => {
              setIsEditing(false);
            }}
          >
            {constants.cancel}
          </Button>
          <Button
            tiny
            disabled={disabled}
            onClick={() => {
              if (disabled) {
                return;
              }

              if (confirm) {
                confirm();
              }
              setIsEditing(false);
            }}
          >
            {constants.save}
          </Button>
        </div>
      )
    );
  };

  return [
    isEditing,
    Edit,
    ConfirmCancel,
    modalOpen,
    UnEditable,
    closeModal,
    setIsEditing,
    Add,
  ] as const;
};

export const useEditableInput = () => {
  type EditableValue = {
    valueKey: string;
    value: string | SelectableItem | null;
  };
  interface InputType extends EditableValue {
    validate?: (value: string) => boolean;
    placeholder?: string;
    value: string | null;
    clearOnFocus?: boolean;
  }

  let editedValues: EditableValue[] = [];

  const setUserSelection = useStore((state) => state.setUserSelection);

  const addEditetValue = (editedValue: EditableValue) => {
    const newValues = editedValues.filter(
      (oldValue) => oldValue.valueKey !== editedValue.valueKey
    );

    newValues.push(editedValue);

    editedValues = newValues;
  };

  const Input = ({ valueKey, value, validate, placeholder, clearOnFocus }: InputType) => {
    const [displayValue, setDisplayValue] = useState<string>(value || "");
    const focusSnapshot = useRef("");

    const isValid = useMemo(() => {
      if (!validate) return true;
      return validate(displayValue);
    }, [displayValue, validate]);

    const classes = [styles.editingValue];
    if (!isValid) classes.push(styles.error);

    return (
      <input
        className={classes.join(" ")}
        placeholder={placeholder}
        value={displayValue}
        onFocus={clearOnFocus ? () => {
          focusSnapshot.current = displayValue;
          setDisplayValue("");
        } : undefined}
        onChange={(e) => {
          setDisplayValue(e.target.value);
          addEditetValue({ valueKey, value: e.target.value });
        }}
        onBlur={clearOnFocus ? () => {
          if (displayValue === "") {
            setDisplayValue(focusSnapshot.current);
            addEditetValue({ valueKey, value: focusSnapshot.current });
          }
        } : undefined}
      />
    );
  };

  const Dropdown = ({
    valueKey,
    value,
    options,
    placeholder,
    clearable,
  }: {
    valueKey: string;
    value: SelectableItem | null;
    placeholder?: string;
    options: SelectableItem[];
    clearable?: boolean;
  }) => {
    const [localValue, setLocalValue] = useState<SelectableItem | null>(value);

    return (
      <DropdownComponent
        options={options}
        onChange={(selected) => {
          setLocalValue(selected);
          addEditetValue({ valueKey, value: selected });
        }}
        onClear={
          clearable
            ? () => {
                setLocalValue(null);
                addEditetValue({ valueKey, value: null });
              }
            : undefined
        }
        placeholder={placeholder}
        value={localValue?.title}
        summary
      />
    );
  };

  const save = () => {
    editedValues.forEach(({ valueKey, value }) => {
      setUserSelection(valueKey, value);
    });
  };

  return [Input, Dropdown, save] as const;
};

export default useEditing;
